import React, { useEffect} from "react";
import { useQuery, useQueries } from 'react-query'
import { getResultsRSC, getUnits } from "data/Querys"
import Loader from 'components/general/Loader'
import PageHeader from "components/common/PageHeader";
import NotAvailable from "components/common/NotAvailable";
import ProgressionRule from "components/common/ProgressionRule";
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";
import Label from "components/general/Label";
import { Tabs, Tab } from "react-bootstrap";

import {  isDressageIndividual,
          isJumpingIndividual,
          isJumpingTeam,
          isDressageIndividualGroupUnit,
          isDressageGrandPrixQualifierDay,
          DRESSAGE_INDIVIDUAL_GROUP_PHASE,    
          DRESSAGE_TEAM_GRAND_PRIX,
          isDressageGranPrixFreestyle} from "components/sports/equ/helper/EquHelpers";

import { getPhaseRSC, getLongPhaseRSC } from "helpers/rsc";


// Jumping
import EquJumping from "components/sports/equ/views/EquJumping";
import EquJumpingTeam from "components/sports/equ/views/EquJumpingTeam";
// Dressage
import EquDressageIndividualAllGroups from "components/sports/equ/views/EquDressageIndividualAllGroups";
import EquDressageTeams from "components/sports/equ/views/EquDressageTeams";
import DressageJuries from "components/sports/equ/events/dressage/DressageJuries";
import { DressageCurrentGP } from "components/sports/equ/events/dressage/DressageCurrentGP";
import { DressagePreviousGP } from "components/sports/equ/events/dressage/DressagePreviousGP";
import EquDressageIndividual from "components/sports/equ/views/EquDressageIndividual";

const EquContext = () => {
  const { mirs_rsc, mirs_competition, mirs_language, mirs_discipline } = useUrlFriendlyToParameters()
  
  useEffect(() => {
    }, []
  )

  //units of a phase for dressage only
  const _phase = isDressageGrandPrixQualifierDay(mirs_rsc) ? DRESSAGE_INDIVIDUAL_GROUP_PHASE: getPhaseRSC(mirs_rsc);
  const { data: unitsData, isError: unitsError, isLoading: unitsLoading } = useQuery(
      {
          queryKey: ['unitsPhase', mirs_competition, mirs_language, _phase, 'N', false],
          queryFn: getUnits,
          refetchInterval: 0,
          enabled: isDressageGrandPrixQualifierDay(mirs_rsc)

      }
  );
  
  //All units results for dressage only
  let queryArray = [];
  const _longPhase = getLongPhaseRSC(mirs_rsc);
  let units = unitsData?.phase?.units?.filter(item => isDressageIndividualGroupUnit(item?.code))
  units?.forEach((unit) => {
      queryArray.push({
          queryKey: ['resultsData', mirs_competition, unit.code, mirs_language],
          queryFn: getResultsRSC,
          enabled: isDressageGrandPrixQualifierDay(mirs_rsc)
      })
  });

  const results = useQueries(queryArray);
  
  // results data for jumping only
  const { data, isError, isLoading } = useQuery(
    {
      queryKey: ['resultsData', mirs_competition, mirs_rsc, mirs_language],
      queryFn: getResultsRSC,
      refetchInterval: 5000,
      staleTime: 5000,
      refetchIntervalInBackground: true,
      notifyOnChangeProps: ['data', 'error'],
      enabled: !isDressageGrandPrixQualifierDay(mirs_rsc),
      onError: (error) =>
        console.error(`Something went wrong: ${error.message}`),
    }
  );
  
  // results data for grand prix teams
  const { data: teamGrandPrixData, isError: teamGrandPrixIsError, isLoading: teamGrandPrixIsLoading } = useQuery(
    {
      queryKey: ['resultsDataTeamGrandPrix', mirs_competition, DRESSAGE_TEAM_GRAND_PRIX, mirs_language],
      queryFn: getResultsRSC,
      refetchInterval: 5000,
      staleTime: 5000,
      refetchIntervalInBackground: true,
      notifyOnChangeProps: ['data', 'error'],
      enabled: isDressageGrandPrixQualifierDay(mirs_rsc),
      onError: (error) =>
        console.error(`Something went wrong: ${error.message}`),
    }
  );


  if (isError || unitsError || teamGrandPrixIsError || results?.some(result => result.isError)) { return <NotAvailable />; }
  if (isLoading || unitsLoading || teamGrandPrixIsLoading || results?.some(result => result.isLoading)) { return <Loader />; }


  if (isError) { return <NotAvailable /> }
  if (isLoading) { return <Loader /> }

  if (!isDressageGrandPrixQualifierDay(mirs_rsc) && !data?.results) { return <NotAvailable /> }
  const notAvailable = !isDressageGrandPrixQualifierDay(mirs_rsc) && !isJumpingTeam(mirs_rsc) && !isDressageIndividual(mirs_rsc) && !!isDressageGranPrixFreestyle(mirs_rsc);
  const phaseTitle = isDressageGrandPrixQualifierDay(mirs_rsc) ? unitsData?.phase?.description : undefined;

  //Dressage
  const resultsFiltered = results?.filter( res => res?.data?.results?.items !== undefined);
  if(isDressageGrandPrixQualifierDay(mirs_rsc) && !resultsFiltered) {return <NotAvailable />}

  const filtersQualRule = {
    type: ['QUALIFICATION'],
    code: ['QUAL_RULE']
  };

  const liveData = isDressageGrandPrixQualifierDay(mirs_rsc) ? resultsFiltered?.find(res => res?.data?.results?.status?.code === 'LIVE')?.data : data;
  
  const currentId = liveData?.results?.extendedInfos?.find(ei => ei?.ei_code === 'CURRENT')?.ei_value;
  const current = liveData?.results?.items?.find(i => i?.participant?.code === currentId);
  const isCurrentTeamMember = current?.eventUnitEntries?.find(eu => eu?.eue_code === 'TEAM_MEMBER')?.eue_value === 'Y';

  const previousId = liveData?.results?.extendedInfos?.find(ei => ei?.ei_code === 'LAST_COMP')?.ei_value;
  const previous = liveData?.results?.items?.find(i => i?.participant?.code === previousId);

  const isPreviousTeamMember = previous?.eventUnitEntries?.find(eu => eu?.eue_code === 'TEAM_MEMBER')?.eue_value === 'Y';
  const previusTeam = isPreviousTeamMember ? teamGrandPrixData?.results?.items?.find(i => i?.teamAthletes?.find(ta => ta?.athlete?.code === previousId)) : undefined;
  const teamAfter = isPreviousTeamMember ? teamGrandPrixData?.results?.extendedInfos?.find(ei => ei?.ei_code === 'TEAM_AFTER' )?.ei_value : undefined;

  return (
    <>
      <PageHeader data={data} title={phaseTitle}/>

      <div className='mirs-page-container'>
      {isJumpingIndividual(mirs_rsc) && <EquJumping data={data} />}

      {isJumpingTeam(mirs_rsc)  && <EquJumpingTeam data={data} />}

      {isDressageGrandPrixQualifierDay(mirs_rsc) &&
        <>
        <DressagePreviousGP data={previous} teamData={previusTeam} teamAfter={teamAfter} officials={liveData?.results?.officials}/>
        <DressageCurrentGP data={current}  officials={liveData?.results?.officials} config={liveData?.config} isCurrentTeamMember={isCurrentTeamMember}/>
        <ProgressionRule key="progress-component" data={resultsFiltered[0]?.data?.config} data2={teamGrandPrixData?.config} filter={filtersQualRule} itemData={'value'} />   
        <Tabs id="mirs-navsport" className="mb-3 mirs-tab-result">
          <Tab eventKey={`1-${mirs_rsc}`} title={<Label data={'HeaderIndividual'} />}>
            <EquDressageIndividualAllGroups results={results} teamData={teamGrandPrixData}/>
          </Tab>
          <Tab eventKey={`2-${mirs_rsc}`} title={<Label data={'HeaderTeam'} />}>
            <EquDressageTeams data={teamGrandPrixData}/>
          </Tab>
        </Tabs>        
        <DressageJuries data={resultsFiltered[0]?.data} />
        </>
      }
      
      {isDressageGranPrixFreestyle(mirs_rsc) &&
      <>
        <DressagePreviousGP data={previous} officials={data?.results?.officials}/>
        <DressageCurrentGP data={current}  officials={data?.results?.officials} config={data?.config} />
        <EquDressageIndividual data={data}  />
        <DressageJuries data={data} />
      </>
      }

      {notAvailable && <NotAvailable/>}
      </div>
    </>
  );
};


export default EquContext;
